import { supabase } from '@/integrations/supabase/client';
import { ReadingFormat } from '@/lib/readingFormatUtils';

export interface ReviewSubmitData {
  rating: number;
  reviewText?: string;
  finishedAt?: Date | null;
  startedAt?: Date | null;
  tags?: string[];
  reading_format?: ReadingFormat;
  currentPage?: number;
  totalPages?: number;
}

export interface ReviewSubmitOptions {
  userId: string;
  bookId: string;
  bookTitle: string;
  bookCoverUrl?: string;
  shelf?: string;
  privacy?: 'public' | 'private';
  isPurchased?: boolean;
}

/**
 * Centralized review/rating submission handler.
 * This ensures consistent behavior across all review entry points:
 * - Creates/updates user_books record
 * - Creates activity for the feed (if public)
 * - Returns whether the record existed before (for activity type determination)
 */
export async function submitReview(
  data: ReviewSubmitData,
  options: ReviewSubmitOptions
): Promise<{ success: boolean; isNew: boolean; userBookId?: string; error?: Error }> {
  const {
    userId,
    bookId,
    bookTitle,
    bookCoverUrl,
    shelf = 'read',
    privacy = 'public',
    isPurchased = false,
  } = options;

  try {
    // Prepare the update data
    const updateData: Record<string, any> = {
      user_id: userId,
      book_id: bookId,
      shelf,
      rating_star: data.rating || null,
      review_text: data.reviewText?.trim() || null,
      privacy,
      tags: data.tags || [],
    };

    if (isPurchased) {
      updateData.is_purchased = true;
    }

    if (data.reading_format) {
      updateData.reading_format = data.reading_format;
    }

    if (data.finishedAt) {
      updateData.finished_at = data.finishedAt.toISOString().split('T')[0];
    } else if (shelf === 'read' && !data.finishedAt) {
      // If marking as read without a date, use today
      updateData.finished_at = new Date().toISOString().split('T')[0];
    }

    if (data.startedAt) {
      updateData.started_at = data.startedAt.toISOString().split('T')[0];
    }

    // When marking as read, set current_page to totalPages to log remaining progress for daily goals
    if (shelf === 'read' && data.totalPages) {
      updateData.current_page = data.totalPages;
      updateData.total_pages = data.totalPages;
    }

    // Check if record exists
    const { data: existingRecord, error: lookupError } = await supabase
      .from('user_books')
      .select('id, rating_star, review_text')
      .eq('user_id', userId)
      .eq('book_id', bookId)
      .maybeSingle();

    if (lookupError) throw lookupError;

    const isNew = !existingRecord?.id;
    const hadReview = !!(existingRecord?.review_text?.trim());
    const hadRating = !!(existingRecord?.rating_star);
    let userBookId: string | undefined;

    // Update or insert
    if (existingRecord?.id) {
      const { data: updated, error: updateError } = await supabase
        .from('user_books')
        .update(updateData)
        .eq('id', existingRecord.id)
        .select('id')
        .single();

      if (updateError) throw updateError;
      userBookId = updated?.id;
    } else {
      const insertData = {
        user_id: userId,
        book_id: bookId,
        shelf,
        rating_star: data.rating || null,
        review_text: data.reviewText?.trim() || null,
        privacy,
        tags: data.tags || [],
        ...(isPurchased && { is_purchased: true }),
        ...(data.reading_format && { reading_format: data.reading_format }),
        ...(data.finishedAt && { finished_at: data.finishedAt.toISOString().split('T')[0] }),
        ...(!data.finishedAt && shelf === 'read' && { finished_at: new Date().toISOString().split('T')[0] }),
        ...(data.startedAt && { started_at: data.startedAt.toISOString().split('T')[0] }),
        // When marking as read, set current_page to totalPages to log remaining progress for daily goals
        ...(shelf === 'read' && data.totalPages && { current_page: data.totalPages, total_pages: data.totalPages }),
      };

      const { data: inserted, error: insertError } = await supabase
        .from('user_books')
        .insert(insertData)
        .select('id')
        .single();

      if (insertError) throw insertError;
      userBookId = inserted?.id;
    }

    // Save tags to book_tags table
    if (data.tags && data.tags.length > 0) {
      try {
        // Delete existing tags for this user and book
        await supabase
          .from('book_tags')
          .delete()
          .eq('book_id', bookId)
          .eq('user_id', userId);

        // Insert new tags
        const tagInserts: { book_id: string; user_id: string; tag_name: string }[] = data.tags.map(tag => ({
          book_id: bookId,
          user_id: userId,
          tag_name: tag.toLowerCase().trim()
        }));

        await supabase.from('book_tags').insert(tagInserts);
      } catch (tagError) {
        console.warn('Tag save failed (non-blocking):', tagError);
      }
    }

    // NOTE: Activity creation is handled by database triggers on user_books table
    // - handle_rating_activity: creates rating_created/rating_updated for public ratings
    // - handle_review_activity: creates review_created for new public reviews
    // - handle_shelf_change_activity: creates book_started/book_finished/book_dnf

    // Update streak for meaningful reading activity (non-blocking)
    try {
      await supabase.functions.invoke('update-streak', {
        body: { userId }
      });
    } catch (streakError) {
      console.warn('Streak update failed (non-blocking):', streakError);
    }

    return { success: true, isNew, userBookId };
  } catch (error) {
    console.error('Error submitting review:', error);
    return { 
      success: false, 
      isNew: false, 
      error: error instanceof Error ? error : new Error('Unknown error') 
    };
  }
}

/**
 * Hook that provides the submitReview function with access to auth context
 */
export function useReviewSubmit() {
  return { submitReview };
}
